import type { Award, BodyMeasurement, CookingMethod, Workout } from '@gramas/shared'
import Dexie, { type EntityTable } from 'dexie'

/**
 * The local database is the source of truth for the user's own diary: every
 * write lands here first and is pushed to the server afterwards, so the app
 * works identically with or without a connection.
 *
 * It is deliberately not a mirror of the server. The catalog holds tens of
 * thousands of products and will keep growing; the phone keeps the diary, the
 * recipes, the favourites and every barcode its owner has actually scanned —
 * a few megabytes. Anything else is cache, tagged with `cachedAt` and evicted.
 *
 * `dirty` is stored as 0/1 rather than a boolean because IndexedDB cannot index
 * booleans, and the push query needs an index to stay cheap. `pinned` is 0/1
 * for the same reason.
 */

export interface LocalFood {
  id: string
  nameLt: string | null
  nameEn: string | null
  brand: string | null
  kcal: number
  protein: number
  carbs: number
  fat: number
  sugars: number | null
  satFat: number | null
  fiber: number | null
  salt: number | null
  servingSizeG: number | null
  /** What one of them weighs, for foods people count instead of weighing. */
  pieceWeightG?: number | null
  servingName: string | null
  /** Grams unless the product is declared and sold by volume. */
  unit: 'g' | 'ml'
  isVerified: boolean
  visibility: 'public' | 'private'
  /** Lowercased, diacritic-free haystack for offline substring search. */
  searchKey: string
  updatedAt: string
  deletedAt: string | null
  /**
   * When this row last entered the cache, refreshed every time the food is
   * used. One field answers both eviction questions — how old is it, and how
   * long since it mattered. Null marks the user's own foods, which are not
   * cache and are never evicted.
   */
  cachedAt: string | null
  /** Referenced by the user's data or favourited. Pinned rows never evict. */
  pinned: 0 | 1
  dirty: 0 | 1
}

export interface LocalBarcode {
  code: string
  symbology: string
  foodId: string
  boundAt: string
  /**
   * Set when this device scanned the code, null when sync merely delivered the
   * binding. Only a personal scan pins the food behind it, so a catalog-wide
   * barcode feed cannot pin the whole catalog.
   */
  scannedAt: string | null
  dirty: 0 | 1
  deletedAt: string | null
}

export interface LocalLogEntry {
  id: string
  logDate: string
  loggedAt: string | null
  loggedTz: string | null
  slot: 'breakfast' | 'lunch' | 'dinner' | 'snack'
  foodId: string | null
  recipeId: string | null
  fastfoodBuildId: string | null
  quantityG: number
  servings: number | null
  snapshotName: string
  kcal: number
  protein: number
  carbs: number
  fat: number
  sugars: number | null
  satFat: number | null
  fiber: number | null
  salt: number | null
  notes: string | null
  clientEditedAt: string
  deletedAt: string | null
  dirty: 0 | 1
}

export interface LocalRecipeIngredient {
  id: string
  foodId: string
  rawWeightG: number
  position: number
  /** How this one ingredient met heat. Null is "not stated", not 'raw'. */
  cooking?: CookingMethod | null
  /**
   * Set on a row the server has retired. Older servers shipped tombstones
   * inside the recipe, and a phone that synced back then still holds them, so
   * every reader goes through `liveIngredients` rather than trusting the array.
   */
  deletedAt?: string | null
}

export interface LocalRecipe {
  id: string
  name: string
  notes: string | null
  cookedWeightG: number
  kcal: number
  protein: number
  carbs: number
  fat: number
  sugars: number | null
  satFat: number | null
  fiber: number | null
  salt: number | null
  ingredients: LocalRecipeIngredient[]
  clientEditedAt: string
  deletedAt: string | null
  dirty: 0 | 1
}

export interface LocalGoal {
  id: string
  effectiveFrom: string
  kcalTarget: number
  proteinG: number | null
  carbsG: number | null
  fatG: number | null
  fiberG: number | null
  // Ceilings. Null means "use the EU reference intake for this calorie target"
  // rather than "no limit" — see defaultSecondaryTargets in @gramas/shared.
  sugarsG: number | null
  satFatG: number | null
  saltG: number | null
  clientEditedAt: string
  deletedAt: string | null
  dirty: 0 | 1
}

export interface LocalWearableDay {
  summaryDate: string
  provider: 'manual' | 'gadgetbridge' | 'huawei' | 'huawei_ocr'
  activeKcal: number | null
  restingKcal: number | null
  steps: number | null
  dirty: 0 | 1
}

/**
 * These two extend the wire types instead of restating them. The older tables
 * above cannot: they were written before the shared schemas and rename the
 * nutrients on the way in. Body measurements and workouts have the same shape
 * on both sides, so inheriting is what stops the two drifting apart silently.
 */
export interface LocalBodyMeasurement extends BodyMeasurement {
  dirty: 0 | 1
}

export interface LocalWorkout extends Workout {
  dirty: 0 | 1
}

export interface LocalAward extends Award {
  dirty: 0 | 1
}

export type FavouriteKind = 'food' | 'recipe' | 'build'

export interface LocalFavourite {
  /** `food:<id>` — the same reference key the usage table is keyed by. */
  id: string
  foodId: string | null
  recipeId: string | null
  buildId: string | null
  addedAt: string
}

/**
 * What the quick-add list is built from: how recently something was logged and
 * how often. Kept apart from the rows themselves because it spans foods,
 * recipes and builds, and because a counter bumped on every log would otherwise
 * mark a synced row dirty and push it back to the server for nothing.
 */
export interface LocalUsage {
  /** `food:<id>`, matching LocalFavourite.id. */
  id: string
  kind: FavouriteKind
  refId: string
  lastUsedAt: string
  count: number
}

/**
 * Side-effect jobs that outlive a page load: an OCR scan taken offline that
 * should be re-checked against the server, or a product to contribute back.
 */
export interface OutboxJob {
  id?: number
  kind: 'ocr-reverify' | 'off-contribute'
  payload: unknown
  createdAt: string
  attempts: number
  lastError: string | null
}

export interface MetaRow {
  key: string
  value: unknown
}

class GramasDb extends Dexie {
  foods!: EntityTable<LocalFood, 'id'>
  barcodes!: EntityTable<LocalBarcode, 'code'>
  logEntries!: EntityTable<LocalLogEntry, 'id'>
  recipes!: EntityTable<LocalRecipe, 'id'>
  goals!: EntityTable<LocalGoal, 'id'>
  wearableDaily!: EntityTable<LocalWearableDay, 'summaryDate'>
  bodyMeasurements!: EntityTable<LocalBodyMeasurement, 'id'>
  workouts!: EntityTable<LocalWorkout, 'id'>
  awards!: EntityTable<LocalAward, 'id'>
  favourites!: EntityTable<LocalFavourite, 'id'>
  usage!: EntityTable<LocalUsage, 'id'>
  outbox!: EntityTable<OutboxJob, 'id'>
  meta!: EntityTable<MetaRow, 'key'>

  constructor() {
    super('gramas')
    this.version(1).stores({
      foods: 'id, searchKey, updatedAt, dirty',
      barcodes: 'code, foodId, dirty',
      logEntries: 'id, logDate, [logDate+slot], dirty, clientEditedAt',
      recipes: 'id, name, dirty',
      goals: 'id, effectiveFrom, dirty',
      wearableDaily: 'summaryDate, dirty',
      outbox: '++id, kind, createdAt',
      meta: 'key',
    })

    // Phones in the field are on version 1 with a full catalog mirror on them,
    // so this is a new version with a migration rather than an edit above.
    this.version(2)
      .stores({
        // `pinned` stands alone as well as leading the compound index: a null
        // cachedAt leaves a row out of the compound one entirely, and the pin
        // recomputation has to be able to see the user's own foods.
        foods: 'id, searchKey, updatedAt, dirty, pinned, [pinned+cachedAt]',
        barcodes: 'code, foodId, dirty, scannedAt',
        bodyMeasurements: 'id, measuredOn, dirty, clientEditedAt',
        workouts: 'id, logDate, externalId, dirty, clientEditedAt',
        favourites: 'id, addedAt, foodId',
        usage: 'id, lastUsedAt, count',
      })
      .upgrade(async (tx) => {
        // Visibility is what sorts the mirror from the user's own work: the
        // server only ever sends a private food to the account that created it,
        // so a private row here is theirs and keeps no expiry. Everything else
        // becomes cache dated to its last catalog write, which puts an
        // untouched mirror past the age limit on the very first prune.
        //
        // Nothing is dropped here. The prune re-derives the pins from the
        // diary, the recipes, the favourites and the scans before it evicts, so
        // a mirrored install shrinks without losing a row anyone can reach.
        await tx
          .table<LocalFood>('foods')
          .toCollection()
          .modify((food) => {
            const own = food.visibility === 'private'
            food.cachedAt = own ? null : food.updatedAt
            food.pinned = own ? 1 : 0
          })
        await tx
          .table<LocalBarcode>('barcodes')
          .toCollection()
          .modify((barcode) => {
            barcode.scannedAt = null
          })
      })

    // Units. Nothing about the indexes changes, but every cached row predates
    // the column and would otherwise render a bottle of water in grams.
    this.version(3).upgrade(async (tx) => {
      await tx
        .table<LocalFood>('foods')
        .toCollection()
        .modify((food) => {
          food.unit = /\bml\b/i.test(food.servingName ?? '') ? 'ml' : 'g'
        })
    })

    // Glitimė's trophy cabinet. A brand-new store, same as workouts was in
    // version 2 — nothing to upgrade, since no install has ever held a row here.
    this.version(4).stores({
      awards: 'id, awardKey, dirty, clientEditedAt',
    })
  }
}

export const db = new GramasDb()

/** Favourites and usage point at three different tables from one key space. */
export function refKey(kind: FavouriteKind, refId: string): string {
  return `${kind}:${refId}`
}

/** Fold diacritics and case so "varškė" and "varske" find each other. */
export function searchKeyFor(...parts: Array<string | null | undefined>): string {
  return parts
    .filter(Boolean)
    .join(' ')
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .trim()
}

export async function getMeta<T>(key: string, fallback: T): Promise<T> {
  const row = await db.meta.get(key)
  return row === undefined ? fallback : (row.value as T)
}

export async function setMeta(key: string, value: unknown): Promise<void> {
  await db.meta.put({ key, value })
}

/** Meta the diary belongs to, as opposed to the install: cleared with the data. */
const ACCOUNT_META_KEYS = [
  'syncCursor',
  'lastSyncedAt',
  'lastPrunedAt',
  'profile',
  'fastfood.usual',
  'fastfood.cup',
  // Whoever the phone was handed to has not seen the tour, whatever the person
  // before them decided about it.
  'tour.dismissedVersion',
  // Nor met Glitimė — that is a property of the account, not the install.
  // (Sass rotation memory is namespaced by user id instead, in sass/engine.ts
  // — it never bleeds between accounts, so it has no entry to clear here.)
  'glitime.metAt',
  // Whose week has been read: the other person's Sunday report is not this
  // person's, and inheriting it would silently swallow their first one.
  'wrapped.seenWeek',
] as const

/**
 * Hand this device to whoever just signed in.
 *
 * Two people share a phone here, and nothing used to notice the swap: the
 * previous account's diary stayed in IndexedDB, its unsent rows were pushed
 * into the new account (the server stamps the user from the session, so they
 * landed as the new user's meals), and the stored cursor — a position in the
 * *other* account's sequence — was already past everything the new one had, so
 * their own diary never came down.
 *
 * Only a genuine change of account clears anything. Signing out and back in as
 * yourself keeps the phone exactly as it was, including anything not yet synced.
 */
export async function adoptUser(userId: string): Promise<void> {
  const previous = await getMeta<string | null>('sessionUserId', null)
  if (previous === userId) return
  if (previous != null) {
    await db.transaction(
      'rw',
      [
        db.logEntries,
        db.foods,
        db.barcodes,
        db.recipes,
        db.goals,
        db.wearableDaily,
        db.bodyMeasurements,
        db.workouts,
        db.awards,
        db.favourites,
        db.usage,
      ],
      async () => {
        await Promise.all([
          db.logEntries.clear(),
          db.foods.clear(),
          db.barcodes.clear(),
          db.recipes.clear(),
          db.goals.clear(),
          db.wearableDaily.clear(),
          db.bodyMeasurements.clear(),
          db.workouts.clear(),
          db.awards.clear(),
          db.favourites.clear(),
          db.usage.clear(),
        ])
      },
    )
    await db.meta.bulkDelete([...ACCOUNT_META_KEYS])
  }
  await setMeta('sessionUserId', userId)
}

/**
 * Ask the browser to exempt our data from eviction. Installed iOS web apps are
 * granted this heuristically, and without it a long gap between uses can cost
 * the user their offline diary.
 */
export async function requestPersistentStorage(): Promise<boolean> {
  if (!navigator.storage?.persist) return false
  if (await navigator.storage.persisted()) return true
  return navigator.storage.persist()
}
